import React, { useState, useEffect, useCallback } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Typography, Box, Grid,
  Select, MenuItem, FormControl, InputLabel, Button, CircularProgress, Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import toast from 'react-hot-toast';
import {
  intervalHtfMappingService, IntervalHtfMapping, SUPPORTED_INTERVALS, intervalLabel,
} from '../../services/intervalHtfMapping.service';

/**
 * Interval → Higher-Timeframe mapping editor.
 *
 * Shared infrastructure shown at the top of the Strategies page regardless of
 * which strategy card is selected. The Sniper strategy uses these mappings to
 * resolve each scan interval's associated Higher Timeframe for trend
 * confirmation. Defaults are server-seeded; the user can override any row.
 */
const TimeframeMappingPanel: React.FC = () => {
  const [rows, setRows] = useState<IntervalHtfMapping[]>([]);
  const [draft, setDraft] = useState<Record<number, number>>({}); // interval -> htfInterval
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await intervalHtfMappingService.list();
      setRows(data);
      const d: Record<number, number> = {};
      for (const r of data) d[r.interval] = r.htfInterval;
      setDraft(d);
    } catch {
      toast.error('Failed to load timeframe mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = rows.some(r => draft[r.interval] !== undefined && draft[r.interval] !== r.htfInterval);

  const handleSave = async () => {
    const changed = rows
      .filter(r => draft[r.interval] !== undefined && draft[r.interval] !== r.htfInterval)
      .map(r => ({ interval: r.interval, htfInterval: draft[r.interval] }));
    if (!changed.length) return;
    setSaving(true);
    try {
      await intervalHtfMappingService.bulkUpsert(changed);
      toast.success(`Saved ${changed.length} timeframe mapping${changed.length === 1 ? '' : 's'}`);
      await load();
    } catch {
      toast.error('Failed to save timeframe mappings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Accordion defaultExpanded={false} sx={{ mb: 2, '&:before': { display: 'none' }, boxShadow: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight={700}>Timeframe Mapping</Typography>
          <Chip
            label="Interval → Higher Timeframe"
            size="small"
            sx={{ fontSize: '0.65rem', fontWeight: 600 }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each scan interval maps to a Higher Timeframe. Strategies that confirm against a
          higher timeframe — like <strong>Sniper</strong> — use this table to know which
          timeframe to check for trend alignment. Defaults are pre-filled; adjust any row and Save.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {SUPPORTED_INTERVALS.map(interval => {
                const htf = draft[interval];
                const original = rows.find(r => r.interval === interval)?.htfInterval;
                const changed = htf !== undefined && original !== undefined && htf !== original;
                return (
                  <Grid key={interval} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>{intervalLabel(interval)} →</InputLabel>
                      <Select
                        label={`${intervalLabel(interval)} →`}
                        value={htf ?? ''}
                        onChange={e => setDraft(prev => ({ ...prev, [interval]: Number(e.target.value) }))}
                        sx={changed ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' } } : undefined}
                      >
                        {SUPPORTED_INTERVALS.map(htfOpt => (
                          <MenuItem key={htfOpt} value={htfOpt}>{intervalLabel(htfOpt)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                );
              })}
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!dirty || saving}
              >
                {saving ? 'Saving…' : 'Save Mappings'}
              </Button>
            </Box>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default TimeframeMappingPanel;
